import * as React from "react";
import {useButton} from "@react-aria/button";
import {useFocusRing} from "@react-aria/focus";
import {useHover} from "@react-aria/interactions";
import clsx from "clsx";
import {XMarkIcon} from "@heroicons/react/20/solid";
import {ENTITY_TYPE_METADATA, EntityType} from "../api/ApprovalApi";

interface ChipProps {
    label: string;
    size?: 'small' | 'medium';
    color?: 'primary' | 'secondary' | 'success' | 'attention' | 'warning';
    variant?: 'outlined' | 'filled';
    icon?: React.ReactNode;
    avatar?: React.ReactNode;
    pattern?: boolean;
    onDelete?: () => void;
    onClick?: () => void;
    disabled?: boolean;
    className?: string;
}

export function Chip(props: ChipProps) {
    const {
        label,
        size = 'medium',
        color = 'primary',
        variant = 'filled',
        icon,
        avatar,
        pattern = false,
        onDelete,
        onClick,
        disabled = false,
        className
    } = props;

    const deleteButtonRef = React.useRef<HTMLButtonElement>(null);
    const chipButtonRef = React.useRef<HTMLButtonElement>(null);

    // If chip is clickable, use button semantics
    const isClickable = !!onClick && !disabled;
    const {buttonProps: chipButtonProps} = useButton(
        {
            onPress: onClick,
            isDisabled: disabled
        },
        chipButtonRef
    );

    // Delete button
    const {buttonProps: deleteButtonProps} = useButton(
        {
            onPress: onDelete,
            isDisabled: disabled,
            'aria-label': 'Delete'
        },
        deleteButtonRef
    );

    const {focusProps, isFocusVisible} = useFocusRing();
    const {hoverProps, isHovered} = useHover({isDisabled: disabled});

    const sizeClasses = {
        small: 'text-xs px-2 py-0.75',
        medium: 'h-8 text-sm px-3 py-1'
    };

    const iconSizeClasses = {
        small: 'w-3.5 h-3.5',
        medium: 'w-4 h-4'
    };

    const avatarSizeClasses = {
        small: 'w-5 h-5',
        medium: 'w-6 h-6'
    };

    const gapClasses = {
        small: 'gap-1',
        medium: 'gap-1.5'
    };

    const baseClasses = clsx(
        'inline-flex items-center rounded-md font-medium ring-1 ring-inset w-fit transition-all duration-200',
        sizeClasses[size],
        gapClasses[size],
        {
            'cursor-pointer': isClickable,
            'cursor-default': !isClickable,
            'opacity-60': disabled
        }
    );

    // Color and variant combinations
    const getVariantClasses = () => {
        const colorMap = {
            primary: {
                filled: 'bg-gray-900 text-white',
                outlined: 'bg-transparent text-gray-900 border border-gray-300'
            },
            secondary: {
                filled: 'bg-gray-200 text-gray-900 ring-gray-500/20',
                outlined: 'bg-transparent text-gray-700 border border-gray-300'
            },
            success: {
                filled: 'bg-green-100 text-green-800 ring-green-500/20',
                outlined: 'bg-transparent text-green-600 border border-green-300'
            },
            warning: {
                filled: 'bg-orange-100 text-orange-800 ring-amber-600/20',
                outlined: 'bg-transparent text-orange-600 border border-orange-300'
            },
            attention: {
                filled: 'bg-red-100 text-red-800 ring-red-600/20',
                outlined: 'bg-transparent text-red-600 border border-red-300'
            }
        };

        return colorMap[color][variant];
    };

    const hoverClasses = isHovered && !disabled ? (() => {
        const hoverMap = {
            primary: {
                filled: 'bg-gray-800',
                outlined: 'bg-gray-50'
            },
            secondary: {
                filled: 'bg-gray-300',
                outlined: 'bg-gray-50'
            },
            success: {
                filled: 'bg-green-200',
                outlined: 'bg-green-50'
            },
            attention: {
                filled: 'bg-red-200',
                outlined: 'bg-red-50'
            },
            warning: {
                filled: 'bg-orange-200',
                outlined: 'bg-orange-50'
            }
        };
        return hoverMap[color][variant];
    })() : '';

    const focusClasses = isFocusVisible && !disabled ? 'ring-2 ring-pink-500 ring-offset-2' : '';

    const patternClasses = pattern ? 'bg-stripe-grid' : '';

    const stripeColorMap = {
        primary: 'rgba(17, 24, 39, 0.25)',
        secondary: 'rgba(107, 114, 128, 0.25)',
        success: 'rgba(34, 197, 94, 0.25)',
        attention: 'rgba(239, 68, 68, 0.25)',
        warning: 'rgba(249, 115, 22, 0.25)',
    };

    const stripeColor = pattern ? stripeColorMap[color] : undefined;

    const chipClasses = clsx(
        baseClasses,
        getVariantClasses(),
        hoverClasses,
        focusClasses,
        patternClasses,
        className
    );

    const ChipContent = (
        <>
            {avatar && (
                <span className={clsx('rounded-full overflow-hidden flex-shrink-0', avatarSizeClasses[size])}>
                    {avatar}
                </span>
            )}

            {!avatar && icon && (
                <span className={clsx('flex-shrink-0', iconSizeClasses[size])}>
                    {icon}
                </span>
            )}

            <span className="truncate">{label}</span>

            {onDelete && (
                <button
                    {...deleteButtonProps}
                    ref={deleteButtonRef}
                    className={clsx(
                        'flex-shrink-0 rounded-full transition-colors duration-150',
                        iconSizeClasses[size],
                        {
                            'hover:bg-black/10': variant === 'filled' && !disabled,
                            'hover:bg-gray-200': variant === 'outlined' && !disabled,
                            'cursor-pointer': !disabled,
                            'cursor-not-allowed': disabled
                        }
                    )}
                    onClick={(e) => e.stopPropagation()}
                >
                    <XMarkIcon className="w-full h-full"/>
                </button>
            )}
        </>
    );

    const commonProps = {
        ...hoverProps,
        ...focusProps,
        className: chipClasses,
        style: {
            '--stripe-color': stripeColor
        } as React.CSSProperties
    };

    if (isClickable) {
        return (
            <button
                {...chipButtonProps}
                {...commonProps}
                ref={chipButtonRef}
            >
                {ChipContent}
            </button>
        );
    }

    return (
        <div
            {...commonProps}
            tabIndex={onDelete ? 0 : undefined}
        >
            {ChipContent}
        </div>
    );
}